import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');
  const [statusFilter, setStatusFilter] = useState<'all' | '连载中' | '已完结'>('all');

  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredComics = useMemo(() => {
    if (!slug) return comics;

    let result = comics.filter((c) => c.categorySlug === slug);

    if (statusFilter !== 'all') {
      result = result.filter((c) => c.status === statusFilter);
    }

    switch (sortBy) {
      case 'latest':
        return [...result].sort(
          (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
        );
      case 'rating':
        return [...result].sort((a, b) => b.rating - a.rating);
      case 'popular':
      default:
        return [...result].sort((a, b) => b.views - a.views);
    }
  }, [slug, sortBy, statusFilter]);

  if (!currentCategory) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-20 pb-20">
          <div className="px-4 md:px-6 max-w-7xl mx-auto text-center">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        <div className="px-4 md:px-6 max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: `${currentCategory.name}漫画`, href: `/category/${slug}` },
            ]}
          />

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">
              共 {filteredComics.length} 部{currentCategory.name}作品
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-8">
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {(['全部', '连载中', '已完结'] as const).map((status) => {
                const filterVal = status === '全部' ? 'all' : status;
                return (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(filterVal as typeof statusFilter)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      statusFilter === filterVal
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-900'
                    }`}
                  >
                    {status}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
              {[
                { key: 'popular' as const, label: '人气' },
                { key: 'latest' as const, label: '最新' },
                { key: 'rating' as const, label: '评分' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setSortBy(opt.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    sortBy === opt.key
                      ? 'bg-accent-100 text-accent-700'
                      : 'text-foreground-500 hover:text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
            {filteredComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} />
            ))}
          </div>

          {filteredComics.length === 0 && (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">暂无符合条件的漫画</p>
            </div>
          )}
        </div>

        <section className="py-10 bg-background-100">
          <div className="px-4 md:px-6 max-w-7xl mx-auto">
            <h3 className="font-heading text-lg text-foreground-900 mb-4">浏览其他分类</h3>
            <div className="flex flex-wrap gap-2">
              {categories
                .filter((c) => c.slug !== slug)
                .map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="px-4 py-2 rounded-full bg-background-50 text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50 transition-colors whitespace-nowrap"
                  >
                    {cat.name} ({cat.count})
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}